import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
  _alpha,
  useTheme,
  Card,
  CardContent,
  CardActions,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  ContentCopy as DuplicateIcon,
  PlayArrow as ApplyIcon,
} from '@mui/icons-material';
import {
  useInvoiceTemplates,
  useCreateInvoiceTemplate,
  useUpdateInvoiceTemplate,
  useDeleteInvoiceTemplate,
  useDuplicateInvoiceTemplate,
  useApplyInvoiceTemplate,
  useInvoices,
} from '../../hooks/useBilling';
import SmartDataGrid from '../common/SmartDataGrid';
import { handleApiError } from '../../utils/errorHandler';

const InvoiceTemplatesPanel = () => {
  const { t } = useTranslation('business');
  const _theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);

  const [templateForm, setTemplateForm] = useState({
    name: '',
    type: 'picking',
    description: '',
    conditions_serialized: '{}',
    lines_serialized: '[]',
  });
  const [applyForm, setApplyForm] = useState({ invoice_id: '' });
  const [duplicateName, setDuplicateName] = useState('');

  const { data: templatesData, isLoading: templatesLoading, refetch: refetchTemplates } = useInvoiceTemplates();
  const { data: invoicesData } = useInvoices();

  const createTemplateMutation = useCreateInvoiceTemplate();
  const updateTemplateMutation = useUpdateInvoiceTemplate();
  const deleteTemplateMutation = useDeleteInvoiceTemplate();
  const duplicateTemplateMutation = useDuplicateInvoiceTemplate();
  const applyTemplateMutation = useApplyInvoiceTemplate();

  const templates = useMemo(() => templatesData?.items || templatesData || [], [templatesData]);
  const invoices = useMemo(() => invoicesData?.items || invoicesData || [], [invoicesData]);

  const filteredTemplates = useMemo(() => {
    if (!searchQuery) return templates;
    const query = searchQuery.toLowerCase();
    return templates.filter(template =>
      template.name?.toLowerCase().includes(query) ||
      template.type?.toLowerCase().includes(query) ||
      template.description?.toLowerCase().includes(query)
    );
  }, [templates, searchQuery]);

  const getTypeColor = (type) => {
    const colors = {
      picking: 'primary',
      receiving: 'secondary',
      packing: 'info',
      storage: 'warning',
      return: 'error',
      inventory: 'success',
    };
    return colors[type?.toLowerCase()] || 'default';
  };

  const handleCreateTemplate= async () => {
    try {
      await createTemplateMutation.mutateAsync(templateForm);
      enqueueSnackbar(t('invoicing.templates.messages.createSuccess'), { variant: 'success' });
      setCreateDialogOpen(false);
      setTemplateForm({ name: '', type: 'picking', description: '', conditions_serialized: '{}', lines_serialized: '[]' });
      refetchTemplates();
    } catch (error) {
      handleApiError(error, enqueueSnackbar, t, 'Error creating template:');
    }
  };

  const handleUpdateTemplate = async () => {
    try {
      await updateTemplateMutation.mutateAsync({ templateId: selectedTemplate.id, data: templateForm });
      enqueueSnackbar(t('invoicing.templates.messages.updateSuccess'), { variant: 'success' });
      setEditDialogOpen(false);
      refetchTemplates();
    } catch (error) {
      handleApiError(error, enqueueSnackbar, t, 'Error updating template:');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (window.confirm(t('invoicing.templates.messages.deleteConfirm'))) {
      try {
        await deleteTemplateMutation.mutateAsync(templateId);
        enqueueSnackbar(t('invoicing.templates.messages.deleteSuccess'), { variant: 'success' });
        setSelectedTemplate(null);
        refetchTemplates();
      } catch (error) {
        handleApiError(error, enqueueSnackbar, t, 'Error deleting template:');
      }
    }
  };

  const handleDuplicateTemplate = async () => {
    try {
      await duplicateTemplateMutation.mutateAsync({ templateId: selectedTemplate.id, newName: duplicateName });
      enqueueSnackbar(t('invoicing.templates.messages.duplicateSuccess'), { variant: 'success' });
      setDuplicateDialogOpen(false);
      setDuplicateName('');
      refetchTemplates();
    } catch (error) {
      handleApiError(error, enqueueSnackbar, t, 'Error duplicating template:');
    }
  };

  const handleApplyTemplate = async () => {
    try {
      await applyTemplateMutation.mutateAsync({ templateId: selectedTemplate.id, invoiceId: applyForm.invoice_id });
      enqueueSnackbar(t('invoicing.templates.messages.applySuccess'), { variant: 'success' });
      setApplyDialogOpen(false);
      setApplyForm({ invoice_id: '' });
    } catch (error) {
      handleApiError(error, enqueueSnackbar, t, 'Error applying template:');
    }
  };

  const openEditDialog = (template) => {
    setSelectedTemplate(template);
    setTemplateForm({
      name: template.name || '',
      type: template.type || 'picking',
      description: template.description || '',
      conditions_serialized: template.conditions_serialized || '{}',
      lines_serialized: template.lines_serialized || '[]',
    });
    setEditDialogOpen(true);
  };

  const openDuplicateDialog = (template) => {
    setSelectedTemplate(template);
    setDuplicateName(`${template.name} (Copy)`);
    setDuplicateDialogOpen(true);
  };

  const openApplyDialog = (template) => {
    setSelectedTemplate(template);
    setApplyDialogOpen(true);
  };

  const templateColumns = [
    { field: 'id', headerName: 'ID', width: 80 },
    { 
      field: 'name', 
      headerName: t('invoicing.templates.columns.name'), 
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">{params.value}</Typography>
      ),
    },
    { 
      field: 'type', 
      headerName: t('invoicing.templates.columns.type'), 
      width: 130,
      renderCell: (params) => (
        <Chip 
          label={t(`invoicing.templates.types.${params.value?.toLowerCase()}`, params.value)} 
          size="small" 
          color={getTypeColor(params.value)}
        />
      ),
    },
    { 
      field: 'description', 
      headerName: t('invoicing.templates.columns.description'), 
      flex: 1.5,
    },
    {
      field: 'actions',
      headerName: t('invoicing.templates.columns.actions'),
      width: 180,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title={t('invoicing.templates.applyTemplate')}>
            <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openApplyDialog(params.row); }}>
              <ApplyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('invoicing.templates.duplicateTemplate')}>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); openDuplicateDialog(params.row); }}>
              <DuplicateIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('invoicing.templates.editTemplate')}>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditDialog(params.row); }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('invoicing.templates.deleteTemplate')}>
            <IconButton 
              size="small" 
              color="error" 
              onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(params.row.id); }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const templateTypes = ['picking', 'receiving', 'packing', 'storage', 'return', 'inventory'];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">{t('invoicing.templates.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('invoicing.templates.subtitle')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            placeholder={t('invoicing.templates.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => refetchTemplates()}>
            {t('common.refresh', 'Refresh')}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)}>
            {t('invoicing.templates.newTemplate')}
          </Button>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1 }}>
        {templatesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredTemplates.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>{t('invoicing.templates.noTemplates')}</Alert>
        ) : (
          <SmartDataGrid
            rows={filteredTemplates}
            columns={templateColumns}
            loading={templatesLoading}
            onRowClick={(params) => setSelectedTemplate(params.row)}
          />
        )}
      </Box>

      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('invoicing.templates.newTemplate')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label={t('invoicing.templates.columns.name')}
              value={templateForm.name}
              onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>{t('invoicing.templates.columns.type')}</InputLabel>
              <Select
                value={templateForm.type}
                label={t('invoicing.templates.columns.type')}
                onChange={(e) => setTemplateForm({ ...templateForm, type: e.target.value })}
              >
                {templateTypes.map(type => (
                  <MenuItem key={type} value={type}>
                    {t(`invoicing.templates.types.${type}`, type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t('invoicing.templates.columns.description')}
              value={templateForm.description}
              onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label={t('invoicing.templates.columns.conditions')}
              value={templateForm.conditions_serialized}
              onChange={(e) => setTemplateForm({ ...templateForm, conditions_serialized: e.target.value })}
              fullWidth
              multiline
              rows={3}
              helperText={t('invoicing.templates.conditionsHelp')}
            />
            <TextField
              label={t('invoicing.templates.columns.lines')}
              value={templateForm.lines_serialized}
              onChange={(e) => setTemplateForm({ ...templateForm, lines_serialized: e.target.value })}
              fullWidth
              multiline
              rows={3}
              helperText={t('invoicing.templates.linesHelp')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleCreateTemplate}>{t('common.create', 'Create')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('invoicing.templates.editTemplate')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label={t('invoicing.templates.columns.name')}
              value={templateForm.name}
              onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>{t('invoicing.templates.columns.type')}</InputLabel>
              <Select
                value={templateForm.type}
                label={t('invoicing.templates.columns.type')}
                onChange={(e) => setTemplateForm({ ...templateForm, type: e.target.value })}
              >
                {templateTypes.map(type => (
                  <MenuItem key={type} value={type}>
                    {t(`invoicing.templates.types.${type}`, type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t('invoicing.templates.columns.description')}
              value={templateForm.description}
              onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label={t('invoicing.templates.columns.conditions')}
              value={templateForm.conditions_serialized}
              onChange={(e) => setTemplateForm({ ...templateForm, conditions_serialized: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label={t('invoicing.templates.columns.lines')}
              value={templateForm.lines_serialized}
              onChange={(e) => setTemplateForm({ ...templateForm, lines_serialized: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleUpdateTemplate}>{t('common.save', 'Save')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={duplicateDialogOpen} onClose={() => setDuplicateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('invoicing.templates.duplicateTemplate')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Alert severity="info">
              {t('invoicing.templates.duplicateInfo', { name: selectedTemplate?.name })}
            </Alert>
            <TextField
              label={t('invoicing.templates.newName')}
              value={duplicateName}
              onChange={(e) => setDuplicateName(e.target.value)}
              fullWidth
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleDuplicateTemplate}>{t('invoicing.templates.duplicate')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={applyDialogOpen} onClose={() => setApplyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('invoicing.templates.applyTemplate')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Alert severity="info">
              {t('invoicing.templates.applyInfo', { name: selectedTemplate?.name })}
            </Alert>
            <FormControl fullWidth>
              <InputLabel>{t('invoicing.templates.selectInvoice')}</InputLabel>
              <Select
                value={applyForm.invoice_id}
                label={t('invoicing.templates.selectInvoice')}
                onChange={(e) => setApplyForm({ invoice_id: e.target.value })}
              >
                {invoices.filter(inv => inv.status === 'draft').map(invoice => (
                  <MenuItem key={invoice.id} value={invoice.id}>
                    {invoice.invoice_number || `INV-${invoice.id}`} - {invoice.customer_name || 'Unknown'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleApplyTemplate} disabled={!applyForm.invoice_id}>
            {t('invoicing.templates.apply')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default React.memo(InvoiceTemplatesPanel);
